import type { JWTTokenPayload } from './types';

type Entity =
  | 'Applications'
  | 'Findings'
  | 'Tests'
  | 'InternalNetwork'
  | 'CodeService'
  | 'Prevention'
  | 'Byoc'
  | 'ByocRegistryPull'
  | 'InternalNetworkTunnel'
  | 'AiCapacity'
  | 'FullRescan';

type Action = 'View' | 'Manage' | 'Execute' | 'Create' | 'Delete' | 'Approve';

const ACTION_IMPLIES: Record<Action, Action[]> = {
  View: [],
  Manage: ['View'],
  Execute: ['View'],
  Create: ['View'],
  Delete: ['View'],
  Approve: [],
};

export const INTERNAL_NETWORK_PENTEST_FEATURE_FLAG =
  'INTERNAL_NETWORK_PENTEST_ENABLED' as const;

export const HYBRID_CODE_SERVICE_FEATURE_FLAG =
  'HYBRID_CODE_SERVICE_ENABLED' as const;

export type AuthFeatureFlagKey =
  | typeof INTERNAL_NETWORK_PENTEST_FEATURE_FLAG
  | typeof HYBRID_CODE_SERVICE_FEATURE_FLAG;

type PermissionDef = {
  readonly name: string;
  readonly description: string;
  readonly action: Action;
  readonly entity: Entity;
  readonly isBuiltIn: false;
};

type BuiltInPermissionDef = {
  readonly name: string;
  readonly description: string;
  readonly isBuiltIn: true;
};

function createPermission(
  action: Action,
  entity: Entity,
  description: string,
  name = `${action} ${entity}`,
): PermissionDef {
  return {
    name,
    description,
    action,
    entity,
    isBuiltIn: false,
  };
}

function builtInPermission(
  name: string,
  description: string,
): BuiltInPermissionDef {
  return {
    name,
    description,
    isBuiltIn: true,
  };
}

export const PERMISSIONS = {
  VIEW_APPLICATIONS: createPermission(
    'View',
    'Applications',
    'View applications, credentials, and integrations',
  ),
  CREATE_APPLICATIONS: createPermission(
    'Create',
    'Applications',
    'Create and onboard new applications',
  ),
  DELETE_APPLICATIONS: createPermission(
    'Delete',
    'Applications',
    'Permanently delete applications',
  ),
  MANAGE_APPLICATIONS: createPermission(
    'Manage',
    'Applications',
    'Edit and configure existing applications and credentials',
  ),
  VIEW_FINDINGS: createPermission(
    'View',
    'Findings',
    'View security findings and reports',
  ),
  MANAGE_FINDINGS: createPermission(
    'Manage',
    'Findings',
    'Update finding status, assign findings, add comments',
  ),
  VIEW_TESTS: createPermission(
    'View',
    'Tests',
    'View test executions and results',
  ),
  EXECUTE_TESTS: createPermission(
    'Execute',
    'Tests',
    'Start and stop security tests',
  ),
  EXECUTE_INTERNAL_NETWORK_SENSOR: createPermission(
    'Execute',
    'InternalNetwork',
    'Report heartbeat and run sensor-scoped internal network operations',
  ),
  VIEW_INTERNAL_NETWORK: createPermission(
    'View',
    'InternalNetwork',
    'View internal network surfaces, signals, graph, and findings',
  ),
  MANAGE_INTERNAL_NETWORK: createPermission(
    'Manage',
    'InternalNetwork',
    'Create and manage internal network surfaces, enrollments, credentials, and data',
  ),
  EXECUTE_CODE_SERVICE_HYBRID: createPermission(
    'Execute',
    'CodeService',
    'Run hybrid code service agent operations including handshake and SQS credential exchange',
  ),
  MANAGE_CODE_SERVICE_HYBRID: createPermission(
    'Manage',
    'CodeService',
    'Enable and manage hybrid code service enrollment for the tenant',
  ),
  MANAGE_PREVENTION: createPermission(
    'Manage',
    'Prevention',
    'Configure prevention connections, coverage, and target mappings',
  ),
  APPROVE_PREVENTION_CONTROL: createPermission(
    'Approve',
    'Prevention',
    'Approve, reject, activate, or deactivate prevention controls',
  ),
  VIEW_BYOC: createPermission(
    'View',
    'Byoc',
    'View BYOC deployment status, cluster health, and connection info',
  ),
  MANAGE_BYOC: createPermission(
    'Manage',
    'Byoc',
    'Configure and manage BYOC onboarding and deployment settings',
  ),
  EXECUTE_BYOC_DEPLOY: createPermission(
    'Execute',
    'Byoc',
    'Report deployment progress from BYOC deploy scripts',
  ),
  EXECUTE_BYOC_REGISTRY_PULL: createPermission(
    'Execute',
    'ByocRegistryPull',
    'Issue short-lived container registry pull credentials for a BYOC cluster',
  ),
  EXECUTE_INTERNAL_NETWORK_TUNNEL: createPermission(
    'Execute',
    'InternalNetworkTunnel',
    "Open a tunnel into this tenant's private network to reach internal applications and hosts",
    'Internal Network Tunnel Access',
  ),
  MANAGE_AI_CAPACITY: createPermission(
    'Manage',
    'AiCapacity',
    'Set the tenant-wide scan effort level that overrides per-application effort settings',
    'Manage AI Capacity',
  ),
  EXECUTE_FULL_RESCAN: createPermission(
    'Execute',
    'FullRescan',
    'Start a full rescan that regenerates discovery, code graphs, and security test cases from scratch',
    'Execute Full Rescan',
  ),

  SSO_ADMIN: builtInPermission(
    'SSO Admin',
    'Descope built-in SSO administration',
  ),
  USER_ADMIN: builtInPermission(
    'User Admin',
    'Descope built-in user administration',
  ),
  IMPERSONATE: builtInPermission(
    'Impersonate',
    'Descope built-in impersonation',
  ),
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;
export type Permission = (typeof PERMISSIONS)[PermissionKey]['name'];

const allPermissions = Object.values(PERMISSIONS);
const permissionNames = allPermissions.map((p) => p.name);
const duplicates = permissionNames.filter(
  (name, index) => permissionNames.indexOf(name) !== index,
);

if (duplicates.length > 0) {
  throw new Error(
    `Duplicate permission names detected: ${duplicates.join(', ')}`,
  );
}

const FEATURE_FLAG_GATED_PERMISSION_KEYS = new Set<PermissionKey>([
  'EXECUTE_INTERNAL_NETWORK_SENSOR',
  'VIEW_INTERNAL_NETWORK',
  'MANAGE_INTERNAL_NETWORK',
  'EXECUTE_CODE_SERVICE_HYBRID',
  'MANAGE_CODE_SERVICE_HYBRID',
]);

const PERMISSION_FEATURE_FLAG_BY_KEY: Partial<
  Record<PermissionKey, AuthFeatureFlagKey>
> = {
  EXECUTE_INTERNAL_NETWORK_SENSOR: INTERNAL_NETWORK_PENTEST_FEATURE_FLAG,
  VIEW_INTERNAL_NETWORK: INTERNAL_NETWORK_PENTEST_FEATURE_FLAG,
  MANAGE_INTERNAL_NETWORK: INTERNAL_NETWORK_PENTEST_FEATURE_FLAG,
  EXECUTE_CODE_SERVICE_HYBRID: HYBRID_CODE_SERVICE_FEATURE_FLAG,
  MANAGE_CODE_SERVICE_HYBRID: HYBRID_CODE_SERVICE_FEATURE_FLAG,
};

export const PERMISSION_DEFINITIONS: ReadonlyArray<{
  name: Permission;
  description: string;
  featureFlagKey?: AuthFeatureFlagKey;
}> = allPermissions
  .filter((p) => !p.isBuiltIn)
  .map((p) => {
    const permissionKey = Object.entries(PERMISSIONS).find(
      ([, definition]) => definition.name === p.name,
    )?.[0] as PermissionKey | undefined;

    return {
      name: p.name as Permission,
      description: p.description,
      ...(permissionKey &&
        FEATURE_FLAG_GATED_PERMISSION_KEYS.has(permissionKey) && {
          featureFlagKey: PERMISSION_FEATURE_FLAG_BY_KEY[permissionKey],
        }),
    };
  });

export function isValidPermission(value: string): value is Permission {
  return permissionNames.includes(value);
}

function buildPermissionHierarchy(): Record<Permission, Permission[]> {
  const hierarchy: Record<string, Permission[]> = {};

  for (const perm of allPermissions) {
    if (perm.isBuiltIn) continue;

    const impliedActions = ACTION_IMPLIES[perm.action];
    const impliedPermissions = impliedActions
      .map((action) => `${action} ${perm.entity}`)
      .filter((name) => permissionNames.includes(name)) as Permission[];

    if (impliedPermissions.length > 0) {
      hierarchy[perm.name] = impliedPermissions;
    }
  }

  return hierarchy as Record<Permission, Permission[]>;
}

export const PERMISSION_HIERARCHY: Readonly<Record<Permission, Permission[]>> =
  buildPermissionHierarchy();

const COCKPIT_PERMISSION_IMPLIES: Readonly<
  Record<string, readonly Permission[]>
> = {
  'Cockpit Admin': ['Evaluation Read', 'Evaluation Execute'],
};

export function getImpliedPermissions(permission: Permission): Permission[] {
  return PERMISSION_HIERARCHY[permission] ?? [];
}

export function permissionSatisfies(
  userPermission: Permission,
  requiredPermission: Permission,
): boolean {
  if (userPermission === requiredPermission) return true;
  const cockpitImplied = COCKPIT_PERMISSION_IMPLIES[userPermission];

  if (cockpitImplied?.includes(requiredPermission)) return true;
  const implied = getImpliedPermissions(userPermission);

  return implied.includes(requiredPermission);
}

export function hasSatisfyingPermission({
  userPermissions,
  requiredPermission,
}: {
  userPermissions: readonly string[] | undefined;
  requiredPermission: string;
}): boolean {
  if (!isValidPermission(requiredPermission)) {
    return false;
  }

  return (userPermissions ?? []).some(
    (userPermission) =>
      isValidPermission(userPermission) &&
      permissionSatisfies(userPermission, requiredPermission),
  );
}

export const ROLES = {
  TENANT_ADMIN: 'Tenant Admin',
  TESTER: 'Tester',
  VIEWER: 'Viewer',
  INTERNAL_NETWORK_SENSOR: 'Internal Network Sensor',
  CODE_SERVICE_HYBRID: 'Code Service Hybrid Mode Access',
  BYOC_DEPLOY_AGENT: 'BYOC Deploy Agent',
  BYOC_REGISTRY_PULL_AGENT: 'BYOC Registry Pull Agent',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export type RoleDefinition = {
  name: Role;
  description: string;
  permissions: Permission[];
  isDescopeDefault?: boolean;
  featureFlagKey?: AuthFeatureFlagKey;
};

export const TENANT_ADMIN_PERMISSIONS: readonly Permission[] =
  PERMISSION_DEFINITIONS.map((p) => p.name);

export const ROLE_DEFINITIONS: ReadonlyArray<RoleDefinition> = [
  {
    name: ROLES.TENANT_ADMIN,
    description:
      'Full administrative access including user management, SSO, and all platform permissions.',
    permissions: [...TENANT_ADMIN_PERMISSIONS],
    isDescopeDefault: true,
  },
  {
    name: ROLES.VIEWER,
    description: 'Read-only access to applications, findings, and tests',
    permissions: [
      PERMISSIONS.VIEW_APPLICATIONS.name,
      PERMISSIONS.VIEW_FINDINGS.name,
      PERMISSIONS.VIEW_TESTS.name,
      PERMISSIONS.VIEW_INTERNAL_NETWORK.name,
      PERMISSIONS.VIEW_BYOC.name,
    ],
  },
  {
    name: ROLES.TESTER,
    description:
      'Set up and configure applications, run tests, and manage findings',
    permissions: [
      PERMISSIONS.VIEW_APPLICATIONS.name,
      PERMISSIONS.CREATE_APPLICATIONS.name,
      PERMISSIONS.MANAGE_APPLICATIONS.name,
      PERMISSIONS.MANAGE_FINDINGS.name,
      PERMISSIONS.EXECUTE_TESTS.name,
      PERMISSIONS.MANAGE_INTERNAL_NETWORK.name,
      PERMISSIONS.MANAGE_PREVENTION.name,
      PERMISSIONS.APPROVE_PREVENTION_CONTROL.name,
      PERMISSIONS.EXECUTE_INTERNAL_NETWORK_TUNNEL.name,
      PERMISSIONS.EXECUTE_FULL_RESCAN.name,
    ],
  },
  {
    name: ROLES.INTERNAL_NETWORK_SENSOR,
    description: 'Customer-deployed internal network sensor agent',
    permissions: [PERMISSIONS.EXECUTE_INTERNAL_NETWORK_SENSOR.name],
    featureFlagKey: INTERNAL_NETWORK_PENTEST_FEATURE_FLAG,
  },
  {
    name: ROLES.CODE_SERVICE_HYBRID,
    description: 'Customer-deployed hybrid code service agent',
    permissions: [PERMISSIONS.EXECUTE_CODE_SERVICE_HYBRID.name],
    featureFlagKey: HYBRID_CODE_SERVICE_FEATURE_FLAG,
  },
  {
    name: ROLES.BYOC_DEPLOY_AGENT,
    description: 'Customer-deployed BYOC deploy script agent',
    permissions: [PERMISSIONS.EXECUTE_BYOC_DEPLOY.name],
  },
  {
    name: ROLES.BYOC_REGISTRY_PULL_AGENT,
    description:
      'Customer-cluster job that refreshes BYOC registry pull credentials',
    permissions: [PERMISSIONS.EXECUTE_BYOC_REGISTRY_PULL.name],
  },
] as const;

export function isValidRole(value: string): value is Role {
  return Object.values(ROLES).includes(value as Role);
}

/**
 * Resolve permissions for a list of role names.
 * Used when Descope returns only roleNames (e.g. in user tenants) and we need
 * to derive permissions for the web app and API.
 * Tenant Admin (Descope default) gets built-in permissions (User Admin, SSO Admin,
 * Impersonate) in addition to our custom permissions.
 */
export function getPermissionsForRoles(roleNames: string[]): Permission[] {
  const result = new Set<Permission>();

  for (const roleName of roleNames) {
    const def = ROLE_DEFINITIONS.find((r) => r.name === roleName);

    if (def) {
      for (const p of def.permissions) {
        result.add(p);
      }

      if (roleName === ROLES.TENANT_ADMIN && def.isDescopeDefault) {
        result.add(PERMISSIONS.USER_ADMIN.name);
        result.add(PERMISSIONS.SSO_ADMIN.name);
        result.add(PERMISSIONS.IMPERSONATE.name);
      }
    }
  }

  return Array.from(result);
}

export function resolveUserPermissions(jwt: JWTTokenPayload): Permission[] {
  return Array.from(new Set<Permission>(jwt.permissions ?? []));
}

export function getOpenIdDefaultPermissions(): Permission[] {
  return [
    ...TENANT_ADMIN_PERMISSIONS,
    PERMISSIONS.USER_ADMIN.name,
    PERMISSIONS.SSO_ADMIN.name,
    PERMISSIONS.IMPERSONATE.name,
  ];
}

export function resolveOpenIdEffectivePermissions({
  extractedPermissions,
  roles,
}: {
  extractedPermissions: string[];
  roles: string[];
}): Permission[] {
  const result = new Set<Permission>();

  for (const value of extractedPermissions) {
    if (isValidPermission(value)) {
      result.add(value);
    }
  }

  for (const permission of getPermissionsForRoles(roles)) {
    result.add(permission);
  }

  if (result.size > 0) {
    return Array.from(result);
  }

  return getOpenIdDefaultPermissions();
}
